import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  useMediaQuery,
  Paper,
  IconButton,
  FormControlLabel,
} from "@mui/material";
import Switch from "@mui/material/Switch";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import InputAdornment from "@mui/material/InputAdornment";
import { toEntityTitleSubtitle } from "../common/string-helpers";

const EditGroupDialog = ({
  open,
  onClose,
  onSave,
  onDelete,
  groupName,
  setGroupName,
  entities,
  selectedEntities,
  onToggleEntity,
  isNewGroup = true,
  meta,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [filterText, setFilterText] = useState("");
  const [nameError, setNameError] = useState(false);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [isRunning, setIsRunning] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Get unavailable entities and their count
  const unavailableEntities = useMemo(() => {
    const availableIds = entities.map(
      (entity) => `${entity.integration}-${entity.id}`
    );
    return selectedEntities.filter((id) => !availableIds.includes(id));
  }, [entities, selectedEntities]);

  const unavailableCount = unavailableEntities.length;

  const handleClose = () => {
    setFilterText("");
    setNameError(false);
    onClose();
  };

  const handleSave = async () => {
    setIsRunning("SAVE");
    if (!groupName.trim()) {
      setNameError(true);
      setIsRunning("");
      return;
    }

    // Uncheck all unavailable entities before saving
    const updatedSelection = selectedEntities.filter((entityId) => {
      const availableIds = entities.map(
        (entity) => `${entity.integration}-${entity.id}`
      );
      return availableIds.includes(entityId);
    });

    setFilterText("");
    setNameError(false);

    try {
      await onSave(updatedSelection);
    } catch (error) {
      console.error("Save failed:", error);
    } finally {
      setIsRunning("");
    }
  };

  const handleNameChange = (e) => {
    setGroupName(e.target.value);
    if (nameError) {
      setNameError(false);
    }
  };

  const handleDelete = () => {
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    setIsRunning("DELETE");
    if (onDelete) {
      try {
        await onDelete();
      } catch (error) {
        console.error("Delete failed:", error);
      } finally {
        setDeleteConfirmOpen(false);
        setIsRunning("");
      }
    }
  };

  const filteredEntities = entities.filter((entity) => {
    const matchesFilter = entity.name
      .toLowerCase()
      .includes(filterText.toLowerCase());
    const isSelected = selectedEntities.includes(
      `${entity.integration}-${entity.id}`
    );
    return showSelectedOnly ? isSelected && matchesFilter : matchesFilter;
  });

  const handleToggleAll = () => {
    const filteredEntityIds = filteredEntities.map(
      (entity) => `${entity.integration}-${entity.id}`
    );

    const allFilteredSelected = filteredEntityIds.every((id) =>
      selectedEntities.includes(id)
    );

    if (allFilteredSelected) {
      // Deselect all filtered entities
      filteredEntityIds.forEach((id) => {
        if (selectedEntities.includes(id)) {
          onToggleEntity(id);
        }
      });
    } else {
      // Select all filtered entities
      filteredEntityIds.forEach((id) => {
        if (!selectedEntities.includes(id)) {
          onToggleEntity(id);
        }
      });
    }
  };

  const isIndeterminate =
    filteredEntities.some((entity) =>
      selectedEntities.includes(`${entity.integration}-${entity.id}`)
    ) &&
    !filteredEntities.every((entity) =>
      selectedEntities.includes(`${entity.integration}-${entity.id}`)
    );

  const isChecked =
    filteredEntities.length > 0 &&
    filteredEntities.every((entity) =>
      selectedEntities.includes(`${entity.integration}-${entity.id}`)
    );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        fullScreen={fullScreen}
        PaperProps={{
          sx: {
            height: fullScreen ? "100%" : "80vh",
            maxHeight: "none",
            bgcolor: theme.palette.background.default,
            "& .MuiDialogContent-root": {
              bgcolor: theme.palette.background.default,
            },
            "& .MuiDialogTitle-root": {
              bgcolor: theme.palette.background.default,
            },
            "& .MuiDialogActions-root": {
              bgcolor: theme.palette.background.default,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
            minHeight: 48,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
            size="small"
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="subtitle1" component="div" sx={{ flex: 1 }}>
            {isNewGroup ? "Create Group" : "Edit Group"}
          </Typography>
        </DialogTitle>

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            p: "16px !important",
            gap: 2,
            overflow: "hidden",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <TextField
              fullWidth
              label="Group Name"
              value={groupName}
              variant="outlined"
              onChange={handleNameChange}
              error={nameError}
              helperText={nameError ? "Group name is required" : ""}
              size="medium"
              InputProps={{
                sx: {
                  backgroundColor: "background.input",
                  borderRadius: "8px",
                },
              }}
              sx={{
                height: "100%",
                "& .MuiOutlinedInput-root": {
                  height: "100%",
                },
              }}
            />
          </Paper>

          {unavailableCount > 0 && (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              {unavailableCount}{" "}
              {unavailableCount === 1 ? "entity is" : "entities are"} no longer
              available. They will be automatically removed upon saving.
            </Alert>
          )}

          <Paper
            elevation={0}
            sx={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              gap: 1,
              p: 1,
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Search entities..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "background.input",
                },
              }}
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1,
                py: 0.5,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Switch
                  checked={showSelectedOnly}
                  onChange={(e) => setShowSelectedOnly(e.target.checked)}
                  size="small"
                />
                <Typography variant="body2">Selected Only</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  edge="start"
                  indeterminate={isIndeterminate}
                  checked={isChecked}
                  onChange={handleToggleAll}
                  size="small"
                />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  Select All
                </Typography>
              </Box>
            </Box>

            <List
              sx={{
                width: "100%",
                bgcolor: "background.paper",
                overflow: "auto",
                flex: 1,
                "& .MuiListItem-root": {
                  py: 0.5,
                },
              }}
            >
              {filteredEntities.map((entity) => {
                const entityId = `${entity.integration}-${entity.id}`;
                const isSelected = selectedEntities.includes(entityId);

                // Get entity title and subtitle using the same helper as EntityCard
                const entityInfo = toEntityTitleSubtitle(entity.name, true);
                const displayTitle =
                  typeof entityInfo === "object"
                    ? entityInfo.title
                    : entityInfo;
                const displaySubtitle =
                  typeof entityInfo === "object"
                    ? entityInfo.subtitle
                    : meta?.integrationNames?.[entity.integration] ||
                      entity.integration;

                return (
                  <ListItem
                    key={entityId}
                    button
                    onClick={() => onToggleEntity(entityId)}
                    sx={{
                      borderRadius: 1,
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Checkbox
                        edge="start"
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple
                        size="small"
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.875rem",
                            lineHeight: 1.2,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {displayTitle}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          noWrap
                          sx={{ mt: 0.5 }}
                        >
                          {displaySubtitle}
                        </Typography>
                      }
                      sx={{ m: 0 }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          {!isNewGroup && onDelete && (
            <Button
              onClick={handleDelete}
              color="error"
              disabled={isRunning !== ""}
              sx={{ mr: "auto", textTransform: "none" }}
              variant="outlined"
              size="small"
              startIcon={<DeleteIcon />}
            >
              Delete Group
            </Button>
          )}
          <Button
            onClick={handleClose}
            disabled={isRunning !== ""}
            variant="outlined"
            size="small"
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={!groupName.trim() || isRunning !== ""}
            size="small"
            sx={{ textTransform: "none" }}
          >
            Save
            {isRunning === "SAVE" && (
              <CircularProgress size={16} sx={{ ml: 1 }} />
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            p: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
            minHeight: 48,
          }}
        >
          <Typography variant="subtitle1">Confirm Delete</Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 2, mt: 1 }}>
          <Typography variant="body2">
            Are you sure you want to delete this group?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "space-between",
            px: 2,
            pb: 2,
          }}
        >
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            size="small"
            variant="outlined"
            sx={{
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={isRunning === "DELETE"}
            size="small"
            sx={{
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
              },
            }}
          >
            Delete
            {isRunning === "DELETE" && (
              <CircularProgress size={16} sx={{ ml: 1 }} />
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditGroupDialog;
